import { USER_ROLES } from '@/constants/userRoles'
import type { Resource } from '@/features/resource/types'
import type { User } from '@/features/user/types/user'

type VisibilityParams = {
  resource: Resource | null | undefined
  loggedInUser: User | null | undefined
}

const RESOURCE_CONFIGURATION_ALLOWED_ROLES: readonly string[] = [
  USER_ROLES.ADMINISTRATORS.value,
  USER_ROLES.TRADERS.value,
]

/**
 * Role-only subset of {@link isResourceConfigurationTabVisible} — the per-resource checks
 * (controlMode === 'LOCAL', battery control-port) are deliberately omitted because the route mount
 * in `ResourcesRoutes` doesn't have a resource in scope. Use this for the route-mount gate so the
 * navigation-side predicate (which DOES check the resource) and the route-side gate agree on who can
 * reach `/configuration`. A non-LOCAL or non-battery resource still lands on the page; the page
 * itself renders the "not available" message in that case.
 */
export function canUserReachResourceConfigurationRoute(loggedInUser: User | null | undefined): boolean {
  return !!loggedInUser?.role && RESOURCE_CONFIGURATION_ALLOWED_ROLES.includes(loggedInUser.role)
}

export function isResourceConfigurationTabVisible({ resource, loggedInUser }: VisibilityParams): boolean {
  // Role gate first — keeps the predicate aligned with the route-mount helper.
  if (!canUserReachResourceConfigurationRoute(loggedInUser)) return false
  if (!resource) return false
  if (resource.controlMode !== 'LOCAL') return false
  return resource.controlPorts.some((port) => port.controlBoxType === 'BATTERY')
}

/**
 * Central (target-consumption) steering config and the local resource.json form are strict opposites
 * for THIS resource + viewer: hide central steering iff the new form takes its place. Anything that
 * hides the form (non-admin, non-LOCAL, non-battery, no resource) keeps central steering visible —
 * including LOCAL-but-non-battery resources, which would otherwise have no editable config at all.
 */
export function isCentralSteeringConfigVisible(params: VisibilityParams): boolean {
  return !isResourceConfigurationTabVisible(params)
}
